import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api, API, scaricaFile } from "@/lib/api";
import { toast } from "sonner";
import { euro, num } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileDown, FileSpreadsheet, ChevronDown, ChevronUp, Clock, Users, CalendarDays, Pencil, Eye, Mail, MailCheck, AlertTriangle, History } from "lucide-react";
import { AnteprimaPdf } from "@/components/AnteprimaPdf";
import { ModificaOreGiorno } from "@/components/ModificaOreGiorno";

const currentMonth = () => new Date().toISOString().slice(0, 7);

const meseLabel = (m) => {
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
};

export default function OreMensili() {
  const [mese, setMese] = useState(currentMonth());
  const [open, setOpen] = useState({});
  const [anteprima, setAnteprima] = useState(null);
  const [editOre, setEditOre] = useState(null);
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const { data: invii = {} } = useQuery({ queryKey: ["ore-mensili-invii", mese], queryFn: async () => (await api.get("/report/ore-mensili/invii", { params: { mese } })).data });
  const inviaMut = useMutation({
    mutationFn: async (dipendente_id) => (await api.post("/report/ore-mensili/invia", { mese, dipendente_id })).data,
    onSuccess: (d, dipendente_id) => {
      qc.invalidateQueries({ queryKey: ["ore-mensili-invii", mese] });
      if (dipendente_id) toast.success(`Ore Mensili inviate a ${d.esiti[0].email}`);
      else toast[d.falliti.length ? "warning" : "success"](`Inviate ${d.inviati} email${d.falliti.length ? ` · non inviate: ${d.falliti.map((f) => `${f.nome} (${f.errore})`).join(", ")}` : ""}`, { duration: 8000 });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Invio non riuscito"),
  });
  const fmtInvio = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "");
  const { data, isLoading } = useQuery({
    queryKey: ["ore-mensili", mese],
    queryFn: async () => (await api.get("/report/ore-mensili", { params: { mese } })).data,
    enabled: /^\d{4}-\d{2}$/.test(mese),
  });

  const url = (fmt) => `${API}/report/ore-mensili/${fmt}?mese=${mese}`;
  const rows = data?.dipendenti || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Ore Mensili</h1>
          <p className="text-slate-500 mt-1">Ore, costo e indennità di trasferta per dipendente nel mese, pronto per il consulente del lavoro.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="text-xs text-slate-400 mb-1">Mese</label>
            <Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="bg-white w-[170px]" data-testid="ore-mensili-mese" />
          </div>
          <Button variant="outline" onClick={() => scaricaFile(url("pdf"), (m) => toast.error(m))} data-testid="ore-mensili-pdf-button">
            <FileDown className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" onClick={() => scaricaFile(url("excel"), (m) => toast.error(m))} data-testid="ore-mensili-excel-button">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button onClick={() => scaricaFile(`${API}/report/conferma-ore/zip?mese=${mese}`, (m) => toast.error(m))} disabled={rows.length === 0} data-testid="ore-mensili-buste-paga-button">
            <FileDown className="h-4 w-4 mr-1.5" /> Ore Mensili (tutti)
          </Button>
          {isAdmin && (
            <Button variant="outline" disabled={rows.length === 0 || inviaMut.isPending} onClick={() => window.confirm(`Inviare via email il PDF Ore Mensili di ${meseLabel(mese)} a tutti i dipendenti con registrazioni nel mese?`) && inviaMut.mutate(null)} data-testid="ore-mensili-invia-tutti-button">
              <Mail className="h-4 w-4 mr-1.5" /> {inviaMut.isPending ? "Invio in corso…" : "Invia via email (tutti)"}
            </Button>
          )}
          <Button variant="outline" onClick={() => scaricaFile(`${API}/report/commercialista/excel?mese=${mese}`, (m) => toast.error(m))} disabled={rows.length === 0} data-testid="ore-mensili-commercialista-button">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Esporta per commercialista
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: CalendarDays, label: "Mese", value: meseLabel(mese), tid: "kpi-mese" },
          { icon: Users, label: "Dipendenti attivi", value: num(rows.length), tid: "kpi-dipendenti" },
          { icon: Clock, label: "Ore totali", value: `${num(data?.ore_totali || 0, 1)} h`, tid: "kpi-ore-totali" },
        ].map((k) => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 stagger-in" data-testid={k.tid}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{k.label}</span>
              <k.icon className="h-5 w-5 text-primary" />
            </div>
            <div className="mt-3 font-display text-2xl font-bold text-slate-900 capitalize">{k.value}</div>
          </div>
        ))}
      </div>

      {isAdmin && rows.some((r) => !r.email) && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm" data-testid="ore-mensili-email-mancanti-avviso">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div><b>{rows.filter((r) => !r.email).length} dipendenti senza email</b>: {rows.filter((r) => !r.email).map((r) => r.nome).join(", ")}. L'invio massivo li salterà: inserisci l'indirizzo in Anagrafica → Dipendenti.</div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        {isLoading ? (
          <div className="py-12 text-center text-slate-400">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-slate-400" data-testid="ore-mensili-empty">Nessuna ora registrata in questo mese.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-3 py-3">Codice</th>
                <th className="px-3 py-3">Dipendente</th>
                <th className="px-3 py-3 text-right">Giorni</th>
                <th className="px-3 py-3 text-right">Ore ord.</th>
                <th className="px-3 py-3 text-right">Ore str.</th>
                <th className="px-3 py-3 text-right">Ore totali</th>
                <th className="px-3 py-3 text-right">Costo ore</th>
                <th className="px-3 py-3 text-right">Pasti</th>
                <th className="px-3 py-3 text-right">Cene</th>
                <th className="px-3 py-3 text-right">Notti trasf.</th>
                <th className="px-3 py-3 text-right">Indennità trasferta</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.dipendente_id}>
                  <tr
                    className="border-b border-slate-50 hover:bg-orange-50/40 cursor-pointer transition-colors"
                    onClick={() => setOpen((o) => ({ ...o, [r.dipendente_id]: !o[r.dipendente_id] }))}
                    data-testid={`ore-mensili-row-${r.codice}`}
                  >
                    <td className="px-3 py-3 font-mono text-xs text-slate-500">{r.codice}</td>
                    <td className="px-3 py-3 font-medium text-slate-800">{r.nome}</td>
                    <td className="px-3 py-3 text-right font-mono">{r.giorni_lavorati}</td>
                    <td className="px-3 py-3 text-right font-mono text-slate-600">{num(r.ore_ordinarie || 0, 2)}</td>
                    <td className="px-3 py-3 text-right font-mono text-primary" data-testid={`ore-straord-${r.codice}`}>{num(r.ore_straordinarie || 0, 2)}</td>
                    <td className="px-3 py-3 text-right font-mono font-semibold text-primary" data-testid={`ore-totali-${r.codice}`}>{num(r.ore_totali, 2)} h</td>
                    <td className="px-3 py-3 text-right font-mono text-slate-500">{euro(r.costo_totale)}</td>
                    <td className="px-3 py-3 text-right font-mono text-amber-700" data-testid={`pasti-${r.codice}`}>{r.pasti_cantiere || 0}</td>
                    <td className="px-3 py-3 text-right font-mono text-sky-700" data-testid={`cene-${r.codice}`}>{r.cene_trasferta || 0}</td>
                    <td className="px-3 py-3 text-right font-mono" data-testid={`notti-${r.codice}`}>{r.notti_trasferta || 0}</td>
                    <td className="px-3 py-3 text-right font-mono text-amber-700" data-testid={`indennita-${r.codice}`}>{euro(r.indennita_trasferta || 0)}</td>
                    <td className="px-3 py-3 text-right text-slate-400 whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); setAnteprima(r); }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline mr-3"
                        title="Anteprima Ore Mensili del dipendente"
                        data-testid={`busta-paga-${r.codice}`}
                      >
                        <Eye className="h-3.5 w-3.5" /> Ore Mensili
                      </button>
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); inviaMut.mutate(r.dipendente_id); }}
                          disabled={inviaMut.isPending}
                          className={`inline-flex items-center gap-1 text-xs font-medium mr-3 hover:underline ${invii[r.dipendente_id] ? "text-green-700" : "text-slate-500"}`}
                          title={invii[r.dipendente_id] ? `Inviato il ${fmtInvio(invii[r.dipendente_id].inviato_il)} a ${invii[r.dipendente_id].email} — clicca per reinviare` : "Invia il PDF Ore Mensili via email al dipendente"}
                          data-testid={`invia-ore-mensili-${r.codice}`}
                        >
                          {invii[r.dipendente_id] ? <><MailCheck className="h-3.5 w-3.5" /> Inviato {fmtInvio(invii[r.dipendente_id].inviato_il)}</> : <><Mail className="h-3.5 w-3.5" /> Invia email</>}
                        </button>
                      )}
                      {open[r.dipendente_id] ? <ChevronUp className="h-4 w-4 inline" /> : <ChevronDown className="h-4 w-4 inline" />}
                    </td>
                  </tr>
                  {open[r.dipendente_id] && (
                    <tr className="bg-slate-50/70" data-testid={`ore-mensili-dettaglio-${r.codice}`}>
                      <td colSpan={12} className="px-3 py-3">
                        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Dettaglio per commessa</div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {r.commesse.map((c) => (
                            <div key={c.commessa_id} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2">
                              <div className="min-w-0">
                                <div className="font-mono text-xs text-slate-500">{c.codice}</div>
                                <div className="text-xs text-slate-700 truncate">{c.descrizione}</div>
                              </div>
                              <span className="font-mono text-sm font-semibold ml-3">{num(c.ore, 2)} h</span>
                              <button
                                onClick={() => setEditOre({ dipendente: r, commessa: c })}
                                className="ml-2 text-slate-400 hover:text-primary"
                                title="Modifica ore di questa commessa"
                                data-testid={`modifica-ore-${r.codice}-${c.codice}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr className="bg-orange-50/60 font-semibold">
                <td className="px-3 py-3" colSpan={3}>TOTALE</td>
                <td className="px-3 py-3 text-right font-mono">{num(rows.reduce((s, r) => s + (r.ore_ordinarie || 0), 0), 2)}</td>
                <td className="px-3 py-3 text-right font-mono">{num(rows.reduce((s, r) => s + (r.ore_straordinarie || 0), 0), 2)}</td>
                <td className="px-3 py-3 text-right font-mono" data-testid="ore-mensili-totale">{num(data.ore_totali, 2)} h</td>
                <td className="px-3 py-3 text-right font-mono">{euro(data.costo_totale)}</td>
                <td className="px-3 py-3 text-right font-mono text-amber-700">{rows.reduce((s, r) => s + (r.pasti_cantiere || 0), 0)}</td>
                <td className="px-3 py-3 text-right font-mono text-sky-700" data-testid="ore-mensili-cene-totale">{rows.reduce((s, r) => s + (r.cene_trasferta || 0), 0)}</td>
                <td className="px-3 py-3 text-right font-mono">{data.notti_totali || 0}</td>
                <td className="px-3 py-3 text-right font-mono text-amber-700" data-testid="ore-mensili-indennita-totale">{euro(data.indennita_totale || 0)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      {isAdmin && rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="ore-mensili-notti">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-semibold text-slate-800"><CalendarDays className="h-4 w-4 text-primary" /> Notti di trasferta · {meseLabel(mese)}
            <span className={`ml-auto text-xs font-normal ${data.giorni_doppi_totali ? "text-amber-700 font-semibold" : "text-slate-500"}`} data-testid="ore-mensili-notti-doppi">{data.giorni_doppi_totali ? `${data.giorni_doppi_totali} giorni con notte doppia da verificare` : "Nessun giorno doppio"}</span></div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100"><th className="px-5 py-2">Dipendente</th><th className="px-5 py-2 text-right">Notti</th><th className="px-5 py-2">Giorni</th><th className="px-5 py-2">Giorni doppi</th></tr></thead>
            <tbody>
              {rows.filter((r) => r.notti_trasferta > 0 || (r.giorni_notte || []).length > 0).map((r) => (
                <tr key={r.dipendente_id} className={`border-b border-slate-50 ${(r.giorni_notte_doppi || []).length ? "bg-amber-50/60" : ""}`} data-testid={`notti-riga-${r.codice}`}>
                  <td className="px-5 py-2 font-medium text-slate-800">{r.nome}</td>
                  <td className="px-5 py-2 text-right font-mono">{r.notti_trasferta}</td>
                  <td className="px-5 py-2 text-xs text-slate-600 font-mono">{(r.giorni_notte || []).map((g) => g.slice(8, 10)).join(" · ") || "—"}</td>
                  <td className="px-5 py-2 text-xs">{(r.giorni_notte_doppi || []).length ? <span className="inline-flex items-center gap-1 text-amber-800 font-semibold" data-testid={`notti-doppi-${r.codice}`}><AlertTriangle className="h-3.5 w-3.5" /> {r.giorni_notte_doppi.map((g) => `${g.slice(8, 10)}/${g.slice(5, 7)}`).join(", ")}</span> : <span className="text-slate-400">—</span>}</td>
                </tr>
              ))}
              {rows.every((r) => !r.notti_trasferta && !(r.giorni_notte || []).length) && <tr><td colSpan={4} className="px-5 py-3 text-slate-400 text-sm">Nessuna notte di trasferta nel mese.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {isAdmin && rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="ore-mensili-storico-invii">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-semibold text-slate-800"><History className="h-4 w-4 text-primary" /> Storico email inviate · {meseLabel(mese)}
            <span className="ml-auto text-xs font-normal text-slate-500" data-testid="ore-mensili-storico-conteggio">{rows.filter((r) => invii[r.dipendente_id]).length} inviati · {rows.filter((r) => !invii[r.dipendente_id]).length} da inviare</span></div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100"><th className="px-5 py-2">Dipendente</th><th className="px-5 py-2">Email</th><th className="px-5 py-2">Stato</th><th className="px-5 py-2">Inviato il</th><th className="px-5 py-2">Da</th></tr></thead>
            <tbody>
              {rows.map((r) => { const inv = invii[r.dipendente_id]; return (
                <tr key={r.dipendente_id} className="border-b border-slate-50" data-testid={`storico-invio-${r.codice}`}>
                  <td className="px-5 py-2 font-medium text-slate-800">{r.nome}</td>
                  <td className="px-5 py-2 text-slate-500 text-xs">{inv?.email || r.email || <span className="text-amber-700">Email mancante</span>}</td>
                  <td className="px-5 py-2">{inv ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5"><MailCheck className="h-3 w-3" /> Inviato</span> : <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"><Mail className="h-3 w-3" /> Non inviato</span>}</td>
                  <td className="px-5 py-2 font-mono text-xs text-slate-600">{inv ? `${inv.inviato_il.slice(8, 10)}/${inv.inviato_il.slice(5, 7)}/${inv.inviato_il.slice(0, 4)} ${inv.inviato_il.slice(11, 16)}` : "—"}</td>
                  <td className="px-5 py-2 text-xs text-slate-500">{inv?.inviato_da || "—"}</td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      )}
      <AnteprimaPdf
        open={!!anteprima}
        onClose={() => setAnteprima(null)}
        url={anteprima ? `${API}/report/busta-paga/pdf?mese=${mese}&dipendente_id=${anteprima.dipendente_id}` : null}
        titolo={anteprima ? `Ore Mensili · ${anteprima.nome} · ${meseLabel(mese)}` : ""}
      />
      <ModificaOreGiorno open={!!editOre} onClose={() => setEditOre(null)} mese={mese} dipendente={editOre?.dipendente} commessa={editOre?.commessa} />
    </div>
  );
}
